/**
 * P2A.3 — Canonical industry bundle manifests. One entry per
 * {@link IndustryTag}. Drives shell edition switcher, onboarding,
 * default landing routes, and entitlement defaults.
 *
 * Module IDs match `docs/module-catalog.json`. Persona IDs match
 * {@link PersonaTag}.
 */
import type { IndustryBundleManifest } from "./index";

export const INDUSTRY_BUNDLES: IndustryBundleManifest[] = [
  {
    id: "medtech",
    displayName: "MedTech",
    summary:
      "FDA 21 CFR Part 820 / ISO 13485 medical device manufacturing — DHR, eDHR, UDI, complaint handling, design-history file links.",
    includedModules: [
      "admin",
      "alarms",
      "batch",
      "compliance",
      "controls",
      "designer",
      "ebr",
      "equipment",
      "historian",
      "integration",
      "maintenance",
      "materials",
      "planning",
      "plm",
      "production",
      "quality",
      "scada",
      "vision",
    ],
    defaultPersonas: [
      "operator",
      "supervisor",
      "quality-engineer",
      "compliance-officer",
      "plm-engineer",
      "executive",
    ],
    simulatorScenarioPacks: ["medtech-dhr", "medtech-complaint", "medtech-capa"],
    evidencePacks: ["21cfr820", "iso13485", "udi", "iec62304"],
    regulatoryStandards: ["21 CFR 820", "ISO 13485", "ISO 14971", "IEC 62304", "EU MDR"],
    defaultLandingRoute: "/workspace/medtech",
    tier: "premium",
    version: "1.0.0",
  },
  {
    id: "pharma-biotech",
    displayName: "Pharma / Biotech",
    summary:
      "GxP-validated batch manufacturing with EBR, electronic signatures, recipe management, and 21 CFR Part 11 audit.",
    includedModules: [
      "admin",
      "alarms",
      "batch",
      "compliance",
      "controls",
      "ebr",
      "equipment",
      "historian",
      "integration",
      "maintenance",
      "materials",
      "planning",
      "plm",
      "production",
      "quality",
      "scada",
    ],
    defaultPersonas: [
      "operator",
      "supervisor",
      "quality-engineer",
      "compliance-officer",
      "plm-engineer",
      "executive",
      "developer-validation",
    ],
    simulatorScenarioPacks: ["pharma-batch", "pharma-recipe-release", "pharma-deviation"],
    evidencePacks: ["21cfr11", "annex11", "ich-q7", "ich-q9"],
    regulatoryStandards: ["21 CFR Part 11", "EU GMP Annex 11", "ICH Q7", "ICH Q9", "ICH Q10"],
    defaultLandingRoute: "/workspace/pharma",
    tier: "enterprise",
    version: "1.0.0",
  },
  {
    id: "food-beverage",
    displayName: "Food & Beverage",
    summary:
      "Food safety, allergen control, traceability (FSMA 204), HACCP, and recall readiness with batch lineage and lot genealogy.",
    includedModules: [
      "admin",
      "alarms",
      "batch",
      "compliance",
      "controls",
      "equipment",
      "historian",
      "integration",
      "maintenance",
      "materials",
      "planning",
      "production",
      "quality",
      "scada",
    ],
    defaultPersonas: [
      "operator",
      "supervisor",
      "quality-engineer",
      "compliance-officer",
      "planner",
      "executive",
    ],
    simulatorScenarioPacks: ["fnb-recall-drill", "fnb-haccp-deviation", "fnb-allergen-changeover"],
    evidencePacks: ["fsma204", "haccp", "sqf", "brc"],
    regulatoryStandards: ["FSMA 204", "HACCP", "SQF", "BRCGS"],
    defaultLandingRoute: "/workspace/food-beverage",
    tier: "core",
    version: "1.0.0",
  },
  {
    id: "specialty-chemicals",
    displayName: "Specialty Chemicals",
    summary:
      "Continuous and batch process manufacturing with reach/SDS compliance, formulation control, and yield optimization.",
    includedModules: [
      "admin",
      "alarms",
      "batch",
      "compliance",
      "controls",
      "equipment",
      "historian",
      "integration",
      "maintenance",
      "materials",
      "planning",
      "plm",
      "production",
      "quality",
      "scada",
    ],
    defaultPersonas: [
      "operator",
      "supervisor",
      "controls-engineer",
      "quality-engineer",
      "compliance-officer",
      "executive",
    ],
    simulatorScenarioPacks: ["chem-batch", "chem-sds-update", "chem-yield-deviation"],
    evidencePacks: ["reach", "ghs", "iso14001"],
    regulatoryStandards: ["REACH", "GHS", "OSHA PSM", "ISO 14001"],
    defaultLandingRoute: "/workspace/specialty-chemicals",
    tier: "premium",
    version: "1.0.0",
  },
  {
    id: "automotive-aerospace",
    displayName: "Automotive / Aerospace",
    summary:
      "IATF 16949 / AS9100 high-mix discrete manufacturing with PPAP, FAI, traceability, andon, and SPC.",
    includedModules: [
      "admin",
      "alarms",
      "compliance",
      "controls",
      "equipment",
      "historian",
      "integration",
      "maintenance",
      "materials",
      "planning",
      "plm",
      "production",
      "quality",
      "scada",
      "vision",
    ],
    defaultPersonas: [
      "operator",
      "supervisor",
      "controls-engineer",
      "vision-engineer",
      "quality-engineer",
      "plm-engineer",
      "planner",
      "executive",
    ],
    simulatorScenarioPacks: ["auto-ppap", "auto-andon", "aero-fai", "auto-spc-excursion"],
    evidencePacks: ["iatf16949", "as9100", "ppap", "fai"],
    regulatoryStandards: ["IATF 16949", "AS9100", "VDA 6.3", "ISO 9001"],
    defaultLandingRoute: "/workspace/automotive-aerospace",
    tier: "enterprise",
    version: "1.0.0",
  },
  {
    id: "electronics-semiconductor",
    displayName: "Electronics / Semiconductor",
    summary:
      "SMT, semiconductor fab, and PCBA manufacturing with WIP tracking, yield analytics, AOI/AOI vision, and SECS/GEM.",
    includedModules: [
      "admin",
      "alarms",
      "compliance",
      "controls",
      "equipment",
      "historian",
      "integration",
      "maintenance",
      "materials",
      "planning",
      "plm",
      "production",
      "quality",
      "scada",
      "vision",
    ],
    defaultPersonas: [
      "operator",
      "supervisor",
      "controls-engineer",
      "vision-engineer",
      "quality-engineer",
      "planner",
      "executive",
    ],
    simulatorScenarioPacks: ["semi-secsgem", "semi-yield-drift", "smt-aoi-defect"],
    evidencePacks: ["ipc-a-610", "j-std-001", "iso9001"],
    regulatoryStandards: ["IPC-A-610", "J-STD-001", "ISO 9001", "SEMI E10"],
    defaultLandingRoute: "/workspace/electronics-semiconductor",
    tier: "premium",
    version: "1.0.0",
  },
  {
    id: "industrial-equipment-discrete",
    displayName: "Industrial Equipment & Discrete",
    summary:
      "Engineer-to-order and configure-to-order discrete manufacturing with serialization, kitting, and field-service handoff.",
    includedModules: [
      "admin",
      "alarms",
      "compliance",
      "controls",
      "equipment",
      "historian",
      "integration",
      "maintenance",
      "materials",
      "planning",
      "plm",
      "production",
      "quality",
      "scada",
    ],
    defaultPersonas: [
      "operator",
      "supervisor",
      "maintenance-technician",
      "quality-engineer",
      "plm-engineer",
      "planner",
      "buyer",
      "warehouse-lead",
      "executive",
    ],
    simulatorScenarioPacks: ["discrete-eto", "discrete-kit-shortage", "discrete-serial-rework"],
    evidencePacks: ["iso9001"],
    regulatoryStandards: ["ISO 9001"],
    defaultLandingRoute: "/workspace/industrial-equipment",
    tier: "core",
    version: "1.0.0",
  },
  {
    id: "multi-site-enterprise",
    displayName: "Multi-Site Enterprise",
    summary:
      "Federated multi-plant deployment with site-rolled-up KPIs, cross-site recipe governance, and central compliance.",
    includedModules: [
      "admin",
      "alarms",
      "batch",
      "compliance",
      "controls",
      "designer",
      "ebr",
      "equipment",
      "historian",
      "integration",
      "maintenance",
      "materials",
      "planning",
      "plm",
      "production",
      "quality",
      "scada",
      "vision",
    ],
    defaultPersonas: [
      "executive",
      "controller",
      "compliance-officer",
      "plm-engineer",
      "integration-admin",
      "supervisor",
    ],
    simulatorScenarioPacks: [
      "multisite-failover",
      "multisite-recipe-promote",
      "multisite-kpi-rollup",
    ],
    evidencePacks: ["soc2", "iso27001", "21cfr11"],
    regulatoryStandards: ["SOC 2", "ISO 27001", "21 CFR Part 11"],
    defaultLandingRoute: "/workspace/enterprise",
    tier: "enterprise",
    version: "1.0.0",
  },
];

export function getIndustryBundle(id: string): IndustryBundleManifest | undefined {
  return INDUSTRY_BUNDLES.find((b) => b.id === id);
}
